/**
 * KAN-744: клиенты менеджера в карточке: первые 20 (список клиентов с
 * фильтром manager) и ссылка на весь список клиентов с этим фильтром.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClientFunnelStage } from '@/components/clients/ClientListCells';
import { clientsApi } from '@/services/api/clients';
import { formatClientPhone } from '@/lib/clientListFormat';

export const MANAGER_CLIENTS_PREVIEW = 20;

interface Props {
  managerId: string;
}

export function ManagerClientsCard({ managerId }: Props): JSX.Element {
  const filters = { manager: managerId, page: 1, page_size: MANAGER_CLIENTS_PREVIEW };
  const { data, isLoading, isError } = useQuery({
    queryKey: ['clients', filters],
    queryFn: () => clientsApi.list(filters),
  });
  const clients = data?.results ?? [];

  let body: JSX.Element;
  if (isLoading) {
    body = <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-400" />;
  } else if (isError) {
    body = <p className="text-sm text-red-600">Не удалось загрузить клиентов</p>;
  } else if (clients.length === 0) {
    body = <p className="text-sm text-gray-500">За менеджером пока нет клиентов</p>;
  } else {
    body = (
      <ul className="divide-y" data-testid="manager-clients-list">
        {clients.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
            <Link to={`/clients/${c.id}`} className="font-medium text-blue-600 hover:underline">
              {c.name || 'Без имени'}
            </Link>
            <span className="text-gray-500">{formatClientPhone(c.phone)}</span>
            <ClientFunnelStage client={c} testId={`manager-client-stage-${c.id}`} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Card data-testid="manager-clients-card">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">
          Клиенты{data ? ` (${data.count})` : ''}
        </CardTitle>
        <Link
          to={`/clients?manager=${encodeURIComponent(managerId)}`}
          className="text-sm text-blue-600 hover:underline"
          data-testid="manager-all-clients-link"
        >
          Все клиенты менеджера
        </Link>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
